import React from 'react';
import { Flex } from 'lib/components/styles/layout';
import { Tooltip, Typography } from 'react-covideo-common';
import { MdInfo } from 'react-icons/md';
import { IoIosWarning } from 'react-icons/io';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components/macro';

interface IAISpotlightUsageProps {
  isLoading: boolean;
  isFetching: boolean;
  spotlightsUsage:
    | {
        left: number;
        max: number;
      }
    | undefined;
}

export const AISpotlightUsage = ({
  isFetching,
  isLoading,
  spotlightsUsage,
}: IAISpotlightUsageProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();

  if (isLoading || isFetching) {
    return (
      <Flex
        justifyContent='center'
        alignItems='center'
        flexDirection='row'
        padding='30px 0 0 0'
        gap='8px'
        style={{ opacity: 0 }}
      >
        <Typography>Loading...</Typography>
        <MdInfo size={18} />
      </Flex>
    );
  }

  if (!spotlightsUsage?.left) {
    return (
      <Flex
        justifyContent='center'
        alignItems='center'
        flexDirection='row'
        padding='30px 0 0 0'
        gap='8px'
      >
        <Typography>
          <Trans
            i18nKey='<usage>{{left}}/{{max}}</usage> Credits left this month'
            values={{
              left: spotlightsUsage?.left,
              max: spotlightsUsage?.max,
            }}
            components={{
              usage: <strong style={{ color: colors.danger[100] }} />,
            }}
          />
        </Typography>
        <Tooltip
          position='bottom'
          popup={
            <span>
              {t("You've used all your credits. Please reach out to add more.")}
            </span>
          }
          extendStyles={{
            popup: {
              width: '240px',
              fontSize: '14px',
              lineHeight: '24px',
              color: colors.neutral[100],
              padding: '8px 12px',
              zIndex: 9999,
            },
            element: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            },
          }}
        >
          <IoIosWarning
            size={24}
            style={{ display: 'inline' }}
            color={colors.danger[100]}
          />
        </Tooltip>
      </Flex>
    );
  }

  return (
    <Flex
      justifyContent='center'
      alignItems='center'
      flexDirection='row'
      padding='30px 0 0 0'
      gap='8px'
    >
      <Typography>
        <Trans
          i18nKey='<usage>{{left}}/{{max}}</usage> Credits left this month'
          values={{
            left: spotlightsUsage?.left,
            max: spotlightsUsage?.max,
          }}
          components={{
            usage: <strong style={{ color: colors.primary[100] }} />,
          }}
        />
      </Typography>
      <Tooltip
        position='bottom'
        popup={
          <span>{t('Creating an AI Flex Video consumes one credit.')}</span>
        }
        extendStyles={{
          popup: {
            width: '180px',
            fontSize: '14px',
            lineHeight: '24px',
            color: colors.neutral[100],
            padding: '8px 12px',
            zIndex: 9999,
          },
          element: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          },
        }}
      >
        <MdInfo
          size={18}
          style={{ display: 'inline' }}
          color={colors.neutral[60]}
        />
      </Tooltip>
    </Flex>
  );
};
